import React from 'react';
import * as Yup from 'yup';

import { TooltipIcon } from '~components';
import { faInfoCircle } from '~components/icon';
import { Column } from '~components/table-view';

// Builds a column header with a trailing info-icon tooltip explaining the field.
// Keeps this file as .ts (not .tsx) per the existing Column[] convention (see fundingLevelsList.ts)
// by using React.createElement instead of JSX syntax.
function headerWithTooltip(label: string, tooltip: string, id: string) {
  return React.createElement(
    React.Fragment,
    null,
    `${label} `,
    React.createElement(TooltipIcon, { id: `sah-budget-header-${id}`, icon: faInfoCircle, tooltip }),
  );
}

const columns: Column[] = [
  {
    id: 'sah_budget_id',
    header: 'Budget ID',
    type: 'text',
    validation: Yup.string().required('Budget ID is required'),
  },
  {
    id: 'start_date',
    header: 'Start Date',
    type: 'date',
    validation: Yup.string().required('Start date is required'),
  },
  {
    id: 'end_date',
    header: 'End Date',
    type: 'date',
    validation: Yup.string()
      .required('End date is required')
      .test('end-after-start', 'End date must be after start date', function (value) {
        const { start_date } = this.parent;
        if (!value || !start_date) return true;
        return new Date(value) >= new Date(start_date);
      }),
  },
  {
    id: 'total_amount',
    header: 'Total',
    type: 'currency',
    classNames: { header: 'header-align-right', cell: 'cell-align-right' },
  },
  {
    id: 'used_amount',
    header: 'Used',
    type: 'currency',
    classNames: { header: 'header-align-right', cell: 'cell-align-right text-danger' },
  },
  {
    id: 'available_amount',
    header: 'Available',
    type: 'currency',
    isEditable: false,
    classNames: { header: 'header-align-right', cell: 'cell-align-right text-success' },
    deriveValue: (row) => (row.total_amount ?? 0) - (row.used_amount ?? 0),
  },
  {
    id: 'prior_spent_amount',
    header: headerWithTooltip(
      'Prior-Spent Amount',
      'Spend a client had with a previous provider before transferring to this budget. Deducted once from their opening balance.',
      'prior-spent-amount',
    ),
    type: 'currency',
    classNames: { header: 'header-align-right', cell: 'cell-align-right' },
    isEditable: (row) => !row.prior_spent_included_in_statements,
    validation: Yup.number()
      .nullable()
      .transform((value, originalValue) => (originalValue === '' ? null : value))
      .min(0, 'Prior-spent amount cannot be negative')
      .test('requires-prior-spent-date', 'Prior-spent date is required when an amount is set', function (value) {
        const { prior_spent_date } = this.parent;
        if (value === undefined || value === null) return true;
        return Boolean(prior_spent_date);
      }),
  },
  {
    id: 'prior_spent_date',
    header: headerWithTooltip(
      'Prior-Spent Date',
      'The date from which the Prior-Spent Amount should be applied to this budget’s opening balance.',
      'prior-spent-date',
    ),
    type: 'date',
    isEditable: (row) => !row.prior_spent_included_in_statements,
    validation: Yup.string()
      .nullable()
      .test('requires-prior-spent-amount', 'Prior-spent amount is required when a date is set', function (value) {
        const { prior_spent_amount } = this.parent;
        if (!value) return true;
        return prior_spent_amount !== undefined && prior_spent_amount !== null;
      }),
  },
  {
    id: 'prior_spent_included_in_statements',
    header: headerWithTooltip(
      'Prior-Spent Applied',
      'Set automatically once a statement has been generated using the Prior-Spent Amount. Cannot be edited.',
      'prior-spent-applied',
    ),
    type: 'checkbox',
    isEditable: false,
    classNames: { header: 'header-align-center', cell: 'cell-align-center' },
  },
  {
    id: 'is_archived',
    header: 'Is Archived',
    type: 'checkbox',
    classNames: { header: 'header-align-center', cell: 'cell-align-center' },
  },
];

export default columns;
